import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  ModalConfig,
  ModalName,
  ModalPropsMap,
  NoPropsModalName,
  PropsModalName
} from "../../../../commons/types/modal";

/**
 * Payload for the showModal action
 */
interface ShowModalPayload {
  name: ModalName;
  contentProps?: ModalPropsMap[ModalName];
}

interface ModalSliceState {
  isOpen: boolean;
  modalConfig: ModalConfig;
}

const initialState: ModalSliceState = {
  isOpen: false,
  modalConfig: null
};

const modalSlice = createSlice({
  name: "modal",
  initialState,
  reducers: {
    close: state => {
      state.isOpen = false;
      state.modalConfig = null;
    },
    /**
     * Shows any modal with type-safe props.
     * Use the exported `showModal` function for type safety.
     */
    show: (state, action: PayloadAction<ShowModalPayload>) => {
      state.isOpen = true;
      state.modalConfig = {
        contentType: action.payload.name,
        contentProps: action.payload.contentProps
      };
    }
  }
});

/**
 * Type-safe action creator for showing modals.
 *
 * TypeScript automatically selects the correct overload based on the modal name:
 * - `NoPropsModalName` (e.g., "about") → no props required
 * - `PropsModalName` (e.g., "auth") → props are required and type-checked
 *
 * @example
 * // Modal without props
 * dispatch(showModal("about"));
 *
 * // Modal with props
 * dispatch(showModal("auth", { onSuccess: () => {} }));
 */
export function showModal<T extends NoPropsModalName>(name: T): ReturnType<typeof modalSlice.actions.show>;
export function showModal<T extends PropsModalName>(
  name: T,
  props: ModalPropsMap[T]
): ReturnType<typeof modalSlice.actions.show>;
export function showModal<T extends ModalName>(name: T, props?: ModalPropsMap[T]) {
  return modalSlice.actions.show({ name, contentProps: props });
}

/**
 * Combined modal actions including `showModal`.
 *
 * `showModal` is a separate function (not generated by createSlice) because it uses
 * function overloads for type-safe props. We export this combined object so consumers
 * can use `modalActions` directly without manual spreading.
 */
export const modalActions = {
  ...modalSlice.actions,
  showModal
};

export default modalSlice;
